import { contextStore } from "../auth/context-store.js";
import { router } from "../router/router.js";
import { platform } from "../../app/browser-platform.js";

class ActiveClientSelector extends HTMLElement {
  private unsubscribe: (() => void) | null = null;
  private isRendering = false;

  connectedCallback() {
    void this.render();
    this.unsubscribe = contextStore.subscribe(() => {
      if (!this.isRendering) {
        void this.render();
      }
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  async render() {
    this.isRendering = true;
    const ctx = contextStore.get();
    let clients: { id: string; name: string }[] = [];

    try {
      if (platform && platform.adminService && ctx.userId) {
        const dummyContext = {
          activeClientId: ctx.activeClientId || undefined,
          permissions: ctx.permissions,
          correlationId: "",
          userId: ctx.userId,
          professionalId: ctx.professionalId || null
        } as any;
        const loaded = await platform.adminService.listAuthorizedClients(dummyContext);
        if (loaded && loaded.length > 0) {
          clients = loaded
            .filter((c: any) => c && c.id && c.status === "ACTIVE" && c.deletedAt === null)
            .map((c: any) => ({
              id: String(c.id),
              name: String(c.name || c.tradeName || c.corporateReason || c.id)
            }));
        }
      }
    } catch (e) {
      console.warn("[SELECTOR] Could not load authorized clients", e);
    }

    if (clients.length === 0) {
      this.innerHTML = `
        <div class="active-client-box" style="display: inline-flex; align-items: center; gap: 6px; padding: 2px 8px; border-radius: 4px; background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.3);">
          <span style="font-size: 0.6875rem; color: #FCA5A5; font-weight: 600;">⚠ Nenhum cliente disponível</span>
        </div>
      `;
      this.isRendering = false;
      return;
    }

    // Set first client if none selected or not matching
    const currentId = ctx.activeClientId || clients[0]?.id;
    const currentName = clients.find(c => c.id === currentId)?.name || clients[0]?.name || "";

    if (!ctx.activeClientId && currentId) {
      contextStore.set({ activeClientId: currentId, activeClientName: currentName });
    } else if (ctx.activeClientName !== currentName && currentName) {
      contextStore.set({ activeClientName: currentName });
    }

    const options = clients.map(c =>
      `<option value="${c.id}" ${currentId === c.id ? 'selected' : ''}>
        ${c.name}
      </option>`
    ).join("");

    this.innerHTML = `
      <div class="active-client-box" style="display: inline-flex; align-items: center; gap: 6px;">
        <span style="font-size: 0.6875rem; color: var(--color-graphite-300, #A8B0B8); text-transform: uppercase; font-weight: 700; letter-spacing: 0.05em;">Cliente:</span>
        <select id="select-active-client" class="client-select" style="background: rgba(255,255,255,0.08); color: var(--color-text-primary, #F2F4F6); border: 1px solid rgba(255,255,255,0.2); padding: 3px 8px; border-radius: 4px; outline: none; cursor: pointer; font-size: 0.8125rem; font-weight: 600; max-width: 220px;">
          ${options}
        </select>
      </div>
    `;

    this.querySelector("#select-active-client")?.addEventListener("change", (e) => {
      const target = e.target as HTMLSelectElement;
      const selectedOption = target.options[target.selectedIndex];
      const selectedName = selectedOption ? selectedOption.text.trim() : "";
      contextStore.set({
        activeClientId: target.value,
        activeClientName: selectedName
      });
      window.dispatchEvent(new CustomEvent("active-client-changed", {
        detail: { clientId: target.value }
      }));
      router.revalidate();
    });

    this.isRendering = false;
  }
}

if (!customElements.get("app-active-client-selector")) {
  customElements.define("app-active-client-selector", ActiveClientSelector);
}
